import type { ReceiveTableRow } from '@/entities/receive';

/**
 * Unit the operator types quantities in. The API only ever accepts inventory
 * units (LTU counts); handling units are an entry convenience converted on the
 * way out via `ltusPerBundle`.
 */
export type ConfirmArrivalUnitMode = 'inventory' | 'handling';

/** Per-line quantities, always held in **inventory units** regardless of mode. */
export interface ConfirmArrivalLineValues {
  lineId: string;
  goodQty: number;
  damagedQty: number;
  lostQty: number;
}

/** The three condition buckets the operator splits a line between. */
export type ConfirmArrivalQuantityField = 'goodQty' | 'damagedQty' | 'lostQty';

export interface ConfirmArrivalFormValues {
  targetLocationId: string;
  /** Storage-area seqno; `null` when the location has none or none is picked. */
  targetLocationSpaceSeq: number | null;
  /** `yyyy-MM-dd` — kept as a string so it round-trips to the API unchanged. */
  actualDateOfReceipt: string;
  comments: string;
  transportDocNumber: string;
  explanationOfLosses: string;
  printGoodsReceivedNote: boolean;
  lines: ConfirmArrivalLineValues[];
}

/**
 * The consignment row the modal was opened from. Only `shipmentId` is required
 * to talk to the backend — the rest is fallback header text for the moment
 * before the context call resolves.
 */
export interface ConfirmArrivalTarget {
  shipmentId: number;
  consignmentNumber?: string;
  waybill?: string;
  truckNumber?: string | null;
}

/** Narrows a dashboard row to the fields the modal needs. */
export const toConfirmArrivalTarget = (row: ReceiveTableRow): ConfirmArrivalTarget | null =>
  row.shipmentId == null
    ? null
    : {
        shipmentId: row.shipmentId,
        consignmentNumber: row.consignmentNumber,
        waybill: row.waybill,
        truckNumber: row.truckNumber
      };
